
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { transformPlayerData } from '@/utils/playerTransform';
import { parsePlayerStats, parsePlayerSkillLevels } from '@/types/player';
import { PlayerCard } from '@/components/PlayerCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, TrendingUp, Trophy, Target } from 'lucide-react';

const PlayerProfile = () => {
  const { playerId } = useParams<{ playerId: string }>();

  const { data: player, isLoading, error } = useQuery({
    queryKey: ['player', playerId],
    queryFn: async () => {
      if (!playerId) throw new Error('Player ID is required');
      
      const { data, error } = await supabase
        .from('players')
        .select(`
          *,
          profile:profiles(first_name, last_name, email)
        `)
        .eq('id', playerId)
        .single();

      if (error) throw error;
      
      return transformPlayerData(data);
    },
    enabled: !!playerId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-lg text-gray-600">Loading player profile...</span>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="text-center py-16">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Player Not Found</h2>
            <p className="text-gray-600">The player profile you're looking for doesn't exist or may have been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {player.profile.first_name} {player.profile.last_name}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary" className="text-sm">
                  {player.position || 'Player'}
                </Badge>
                {player.jersey_number && (
                  <Badge variant="outline" className="text-sm">
                    #{player.jersey_number}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Player Details */}
        <div className="max-w-4xl mx-auto">
          <PlayerCard player={player} canEdit={false} />
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile;
